import React from 'react';

import {useTranslation} from 'react-i18next';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Modal} from 'components/ui-kit/Modal';

import {
    Content,
    DeleteButton,
    DeletedDataItem,
    DeletedDataList,
    DeletedDataTitle,
    Footer,
    GtinAccent,
    InfoWrapper,
    Question,
    WarningCard,
    WarningDescription,
    WarningTitle,
} from './styled';

type DeleteProductModalProps = {
    gtin?: string | number;
    isPending: boolean;
    onClose: () => void;
    onConfirm: () => void;
    renderInline?: boolean;
    show: boolean;
};

export const DeleteProductModal = ({
    gtin,
    isPending,
    onClose,
    onConfirm,
    renderInline = false,
    show,
}: DeleteProductModalProps) => {
    const {t} = useTranslation();
    const title = t('pages.registries.deleteProductModal.title', {
        defaultValue: 'Delete Product',
    });

    const deletedDataItems = [
        t('pages.registries.deleteProductModal.items.productInformation', {
            defaultValue: 'Product information and attributes',
        }),
        t('pages.registries.deleteProductModal.items.brandData', {
            defaultValue: 'Associated brand data',
        }),
        t('pages.registries.deleteProductModal.items.classificationCodes', {
            defaultValue: 'Classification codes (HSN, GPC)',
        }),
        t('pages.registries.deleteProductModal.items.productDescriptions', {
            defaultValue: 'Product descriptions and composition',
        }),
    ];

    const content = (
        <Content>
            <InfoWrapper>
                <WarningCard>
                    <WarningTitle>
                        {t('pages.registries.deleteProductModal.warningTitle', {
                            defaultValue: 'Warning: This action cannot be undone',
                        })}
                    </WarningTitle>
                    <WarningDescription>
                        {t('pages.registries.deleteProductModal.warningDescription', {
                            defaultValue:
                                'All data associated with this product will be permanently deleted from the system.',
                        })}
                    </WarningDescription>
                </WarningCard>
                <Question>
                    {t('pages.registries.deleteProductModal.questionPrefix', {
                        defaultValue: 'Are you sure you want to delete the product with GTIN ',
                    })}
                    <GtinAccent>{gtin ?? '-'}</GtinAccent>
                    {t('pages.registries.deleteProductModal.questionSuffix', {
                        defaultValue: '?',
                    })}
                </Question>
                <DeletedDataTitle>
                    {t('pages.registries.deleteProductModal.deletedDataTitle', {
                        defaultValue: 'The following data will be deleted:',
                    })}
                </DeletedDataTitle>
                <DeletedDataList>
                    {deletedDataItems.map((item) => (
                        <DeletedDataItem key={item}>{item}</DeletedDataItem>
                    ))}
                </DeletedDataList>
            </InfoWrapper>
            <Footer>
                <Button
                    variant={ButtonVariant.Outline}
                    type="button"
                    disabled={isPending}
                    onClick={onClose}
                >
                    {t('components.form.Cancel')}
                </Button>
                <DeleteButton
                    variant={ButtonVariant.Normal}
                    type="button"
                    disabled={isPending}
                    onClick={onConfirm}
                >
                    {t('pages.registries.deleteProductModal.confirmButton', {
                        defaultValue: 'Delete Product',
                    })}
                </DeleteButton>
            </Footer>
        </Content>
    );

    if (renderInline) return content;

    return (
        <Modal show={show} onClose={onClose} modalWidth={1} title={title}>
            {content}
        </Modal>
    );
};
